import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  User,
  Info,
  AlertTriangle,
  Check,
  Settings,
  ChevronRight,
  Mail,
  Users,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
} from "@tabipla/ui";
import { AvatarSettings, ConfirmDialog } from "@tabipla/components";
import { useConfirmDialog } from "@tabipla/hooks/confirm-dialog";
import { DashboardLayout } from "@/presentation/layouts/DashboardLayout";
import { useProfile } from "@/application/hooks/useProfile";
import { changeEmail } from "@/infrastructure/auth/auth-client";

export function ProfilePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { confirm, dialogProps } = useConfirmDialog();
  const {
    user,
    updateProfile,
    deleteAccount,
    avatarInfo,
    updateAvatar,
    uploadAvatarImage,
    deleteAvatarImage,
  } = useProfile();
  const [name, setName] = useState(user?.name || "");
  const [newEmail, setNewEmail] = useState("");
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailChangeStatus, setEmailChangeStatus] = useState<
    "idle" | "pending" | "success" | "error"
  >("idle");
  const [emailChangeError, setEmailChangeError] = useState<string | null>(null);

  // LINE登録ユーザー（ダミーメール）かどうかを判定
  const isDummyEmail = user?.email?.endsWith("@line.invalid") ?? false;

  const handleSave = () => {
    updateProfile.mutate({ name });
  };

  const handleChangeEmail = async () => {
    if (!newEmail || !isValidEmail(newEmail)) return;

    setEmailChangeStatus("pending");
    setEmailChangeError(null);

    try {
      const result = await changeEmail({
        newEmail,
        callbackURL: `${window.location.origin}/profile`,
      });

      if (result.error) {
        setEmailChangeStatus("error");
        setEmailChangeError(result.error.message || t("profile.emailChangeFailed"));
      } else {
        setEmailChangeStatus("success");
        setIsEditingEmail(false);
        setNewEmail("");
      }
    } catch {
      setEmailChangeStatus("error");
      setEmailChangeError(t("profile.emailChangeFailed"));
    }
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSaveAvatar = (emoji: string | null, color: string | null) => {
    updateAvatar.mutate({ avatarEmoji: emoji, avatarColor: color });
  };

  const handleClearAvatar = () => {
    updateAvatar.mutate({ avatarEmoji: null, avatarColor: null });
  };

  const handleImageUpload = async (file: File) => {
    await uploadAvatarImage.mutateAsync(file);
  };

  const handleImageDelete = async () => {
    await deleteAvatarImage.mutateAsync();
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: t("profile.deleteAccount"),
      description: t("profile.deleteConfirm"),
      confirmText: t("common.delete"),
      cancelText: t("common.cancel"),
      destructive: true,
    });
    if (confirmed) {
      deleteAccount.mutate();
    }
  };

  return (
    <DashboardLayout title={t("profile.title")}>
      <div className="animate-fade-in space-y-6">
        {/* Header - Desktop only */}
        <div className="hidden lg:block">
          <h1 className="text-2xl font-bold tracking-tight">{t("profile.title")}</h1>
          <p className="text-muted-foreground">{t("profile.subtitle")}</p>
        </div>

        <div className="space-y-4">
          {/* Settings Link - Mobile only */}
          <Card
            className="cursor-pointer transition-colors hover:bg-muted/50 active:bg-muted lg:hidden"
            onClick={() => navigate("/settings")}
          >
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                  <Settings className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{t("settings.title")}</p>
                  <p className="text-sm text-muted-foreground">{t("settings.subtitle")}</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </CardContent>
          </Card>

          {/* Friends Link */}
          <Card
            className="cursor-pointer transition-colors hover:bg-muted/50 active:bg-muted"
            onClick={() => navigate("/friends")}
          >
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                  <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="font-medium">{t("friends.title")}</p>
                  <p className="text-sm text-muted-foreground">{t("friends.subtitle")}</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center text-lg">
                <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                  <User className="h-4 w-4 text-primary" />
                </div>
                {t("profile.personalInfo")}
              </CardTitle>
              <CardDescription>{t("profile.personalInfoDescription")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("auth.name")}</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("auth.namePlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("auth.email")}</label>
                {isEditingEmail ? (
                  <div className="space-y-2">
                    <Input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder={t("profile.newEmailPlaceholder")}
                      className={newEmail && !isValidEmail(newEmail) ? "border-red-500" : ""}
                    />
                    {newEmail && !isValidEmail(newEmail) && (
                      <p className="text-xs text-red-500">{t("profile.invalidEmail")}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleChangeEmail}
                        disabled={
                          emailChangeStatus === "pending" || !newEmail || !isValidEmail(newEmail)
                        }
                      >
                        {emailChangeStatus === "pending"
                          ? t("profile.sending")
                          : t("profile.sendVerification")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setIsEditingEmail(false);
                          setNewEmail("");
                          setEmailChangeStatus("idle");
                          setEmailChangeError(null);
                        }}
                      >
                        {t("common.cancel")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        value={isDummyEmail ? "" : user?.email || ""}
                        placeholder={
                          isDummyEmail ? t("profile.noEmailSet") : t("auth.emailPlaceholder")
                        }
                        disabled
                        className="bg-muted/50"
                      />
                      <Button size="sm" variant="outline" onClick={() => setIsEditingEmail(true)}>
                        <Mail className="mr-1 h-4 w-4" />
                        {isDummyEmail ? t("profile.setEmail") : t("profile.changeEmail")}
                      </Button>
                    </div>
                    {isDummyEmail && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        {t("profile.lineUserEmailHint")}
                      </p>
                    )}
                  </div>
                )}
                {emailChangeStatus === "success" && (
                  <div className="flex items-center gap-2 rounded-xl bg-green-50 p-3 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
                    <Check className="h-4 w-4" />
                    {t("profile.verificationEmailSent")}
                  </div>
                )}
                {emailChangeStatus === "error" && emailChangeError && (
                  <div className="flex items-center gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
                    <AlertTriangle className="h-4 w-4" />
                    {emailChangeError}
                  </div>
                )}
              </div>
              {!isEditingEmail && (
                <>
                  <Button onClick={handleSave} disabled={updateProfile.isPending}>
                    {updateProfile.isPending ? t("profile.saving") : t("profile.saveChanges")}
                  </Button>
                  {updateProfile.isSuccess && (
                    <div className="flex items-center gap-2 rounded-xl bg-green-50 p-3 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
                      <Check className="h-4 w-4" />
                      {t("profile.profileUpdated")}
                    </div>
                  )}
                  {updateProfile.isError && (
                    <div className="flex items-center gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
                      <AlertTriangle className="h-4 w-4" />
                      {t("error.codes.UPDATE_FAILED")}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Avatar Settings Card */}
          <AvatarSettings
            initialEmoji={avatarInfo.data?.avatarEmoji}
            initialColor={avatarInfo.data?.avatarColor}
            initialImage={avatarInfo.data?.image}
            userName={user?.name}
            isSaving={updateAvatar.isPending}
            isSuccess={updateAvatar.isSuccess}
            errorMessage={updateAvatar.isError ? t("error.codes.UPDATE_FAILED") : undefined}
            onSave={handleSaveAvatar}
            onClear={handleClearAvatar}
            onImageUpload={handleImageUpload}
            onImageDelete={handleImageDelete}
            isUploadingImage={uploadAvatarImage.isPending || deleteAvatarImage.isPending}
          />

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center text-lg">
                <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                  <Info className="h-4 w-4 text-primary" />
                </div>
                {t("profile.accountInfo")}
              </CardTitle>
              <CardDescription>{t("profile.accountInfoDescription")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between rounded-xl border p-4">
                <span className="text-muted-foreground">{t("profile.accountCreated")}</span>
                <span className="font-medium">
                  {user?.createdAt
                    ? new Date(user.createdAt).toLocaleDateString(i18n.language)
                    : t("profile.na")}
                </span>
              </div>
              <div className="flex justify-between rounded-xl border p-4">
                <span className="text-muted-foreground">{t("profile.emailVerified")}</span>
                <span className="font-medium">
                  {user?.emailVerified ? t("profile.yes") : t("profile.no")}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-200 dark:border-red-800">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center text-lg text-red-600 dark:text-red-400">
                <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                  <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                </div>
                {t("profile.dangerZone")}
              </CardTitle>
              <CardDescription>{t("profile.dangerZoneDescription")}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteAccount.isPending}
              >
                {deleteAccount.isPending ? t("profile.deleting") : t("profile.deleteAccount")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <ConfirmDialog {...dialogProps} />
    </DashboardLayout>
  );
}
